'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CgClose, CgSpinner } from 'react-icons/cg';
import HubSpotMeeting from '@/src/components/Contact-us/HubSpotMeeting';
import ThankyouPage from '@/src/components/Contact-us/ThankyouPage';
import { sendAnalyticsData, trackFormSubmitted } from '@/src/utils/analytics';
import { recaptchaKey, supportFormUrl } from '@/src/utils/processenv';
import InputText from '../Input/InputText';
export interface IContactUs {
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  message: string;
}
interface IProps {
  formType: 'contactus' | 'customPricing' | 'enterprise' | 'requestQuote' | 'demo';
}
declare const grecaptcha: any;
export const Contactus = ({
  formType
}: IProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errMessage, setErrMessage] = useState('');
  const [showToast, setShowToast] = useState<'success' | 'error' | 'none'>('none');
  const [showThankYou, setShowThankYou] = useState(false);
  const [showThankYouAfterExit, setShowThankYouAfterExit] = useState(false);
  const [formData, setFormData] = useState<IContactUs | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const formTypeMessages = {
    contactus: 'Thank you for contacting us!',
    customPricing: 'We will get back to you with a custom pricing quote.',
    enterprise: 'Our enterprise team will reach out to you shortly.',
    requestQuote: 'Your quote request has been received.',
    demo: 'Your demo scheduling request has been received.'
  };
  const {
    handleSubmit,
    register,
    formState: {
      errors
    },
    watch
  } = useForm<IContactUs>();
  const activeBtn = !!watch('email')?.length;
  const handleLoading = (loading: boolean) => {
    setIsLoading(loading);
  };
  const handleShowToast = (status: 'success' | 'error' | 'none') => {
    setShowToast(status);
    if (status !== 'none') {
      setTimeout(() => {
        setShowToast('none');
        setErrMessage('');
      }, 3000);
    }
  };
  const executeRecaptcha = () => {
    return new Promise(resolve => {
      if (typeof window !== 'undefined' && typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
        grecaptcha.enterprise.ready(async () => {
          const token = await grecaptcha.enterprise.execute(recaptchaKey as string, {
            action: 'LOGIN'
          });
          resolve(token);
        });
      } else {
        // Fallback if reCAPTCHA is not loaded
        resolve('fallback-token');
      }
    });
  };
  const onSubmit = async (data: IContactUs) => {
    handleLoading(true);
    const token_0 = await executeRecaptcha();

    // For demo form, merge firstName and lastName into name for API compatibility
    const apiPayload = {
      ...data,
      name: formType === 'demo' && data.firstName ? `${data.firstName} ${data.lastName || ''}`.trim() : data.name,
      token: token_0,
      formType,
      timeStamp: new Date()
    };
    try {
      // eslint-disable-next-line no-undef
      await fetch(supportFormUrl as string, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiPayload)
      }).then(response => response.json()).then(resData => {
        if (resData.success) {
          // Keep firstName and lastName for HubSpot widget
          setFormData(data);
          setShowThankYou(true);
        } else {
          setErrMessage(resData?.message);
          handleShowToast('error');
        }
      });
      sendAnalyticsData(`FORM_SUBMITTED_FROM_${formType.toUpperCase()}`);
      trackFormSubmitted(formType, data.email);
    } catch {
      // Remove unused variable
      handleShowToast('error');
    } finally {
      handleLoading(false);
    }
  };
  useEffect(() => {
    // Load reCAPTCHA script only on client side
    if (typeof window !== 'undefined') {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaKey}`;
      script.async = true;
      document.body.appendChild(script);
      return () => {
        // Clean up script on unmount - safer approach
        try {
          const existingScript = document.querySelector(`script[src*="recaptcha"]`);
          if (existingScript?.parentNode) {
            existingScript.parentNode.removeChild(existingScript);
          }
        } catch (error) {
          // Ignore cleanup errors
          console.warn('Script cleanup failed:', error);
        }
      };
    }
    return () => {
      // Empty cleanup function for server-side
    };
  }, []);
  const handleMeetingExit = () => {
    sendAnalyticsData('HUBSPOT_MEETING_EXIT_CLICKED');
    setShowThankYouAfterExit(true);
  };

  // Show thank you page after successful submission
  // For demo form, show HubSpot widget directly (skip thank you page)
  if (showThankYou) {
    if (formType === 'demo') {
      if (showThankYouAfterExit) {
        return <ThankyouPage formData={formData} />;
      }
      return <HubSpotMeeting formData={formData} onExit={handleMeetingExit} />;
    }
    return <ThankyouPage formData={formData} />;
  }
  return <div className="mb-20 mt-16 flex h-full min-h-[calc(100vh-8rem)] flex-col text-[#3D3E40]">
      <div className="flex-shrink-0 py-12 text-center md:pt-16">
        <h1 className="text-2xl font-semibold transition-colors duration-200 ease-out will-change-auto md:text-3xl">
          {formType === 'requestQuote' && 'Get a Quote' || formType === 'demo' && 'AbleSpace Demo' || 'Contact Us'}
        </h1>
        {formType === 'requestQuote' ? <p className="animate-in fade-in-0 mt-1 text-sm delay-200 duration-500">
            We will send you a quote within 24 hours
          </p> : null}
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="animate-in fade-in-0 slide-in-from-bottom-2 flex flex-1 flex-col items-center px-6 delay-150 duration-500">
        {formType === 'demo' ? <div className="animate-in fade-in-0 slide-in-from-bottom-2 mb-8 flex w-full gap-4 delay-200 duration-500 md:w-96">
            <div className="flex-1">
              <InputText name="firstName" label="First Name" type="text" register={register} placeholder="First name..." errors={errors} required={{
            value: true,
            message: 'Please enter your first name'
          }} />
            </div>
            <div className="flex-1">
              <InputText name="lastName" label="Last Name" type="text" register={register} placeholder="Last name..." errors={errors} required={{
            value: true,
            message: 'Please enter your last name'
          }} />
            </div>
          </div> : <div className="animate-in fade-in-0 slide-in-from-bottom-2 mb-8 w-full delay-200 duration-500 md:w-96">
            <InputText name="name" label="Name" type="text" register={register} placeholder="Enter name..." />
          </div>}
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 mb-8 w-full delay-300 duration-500 md:w-96">
          <InputText name="email" label="Email" type="text" register={register} placeholder="Enter email..." errors={errors} required={{
          value: true,
          message: 'Please enter your email address'
        }} pattern={{
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Please enter a valid email address'
        }} />
        </div>
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 delay-400 mb-8 w-full duration-500 md:w-96">
          <InputText name="phone" label="Phone" type="text" register={register} placeholder="Enter phone number..." errors={errors} pattern={{
          value: /^[0-9\s-]+$/,
          message: 'Invalid phone number format'
        }} />
        </div>
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 mb-10 w-full delay-500 duration-500 md:w-96">
          <label htmlFor={`${formType}-message`} className="text-sm font-medium text-gray-700">
            {formType === 'requestQuote' ? 'Please share the name of your school/district, the count of teachers/therapists, and the approximate number of tracked students.' : 'Tell me about your requirements'}
          </label>
          <textarea {...register('message')} id={`${formType}-message`} className="mt-1 h-32 w-full rounded-md border border-[#e3e5e5] bg-white px-3 py-2 font-medium outline-none transition-colors duration-150 ease-out will-change-auto placeholder:text-[#A4A5A8] focus:border-2 focus:border-blue-500 md:px-4 md:py-2.5 md:font-normal"></textarea>
        </div>
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 delay-[550ms] mb-6 w-full duration-500 md:w-96">
          <label className="flex cursor-pointer items-start gap-3">
            <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer accent-[#318AE5]" />
            <span className="text-sm text-gray-600">
              By submitting this form, I acknowledge that I have read and agree
              to the{' '}
              <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer" className="font-semibold !text-[#318AE5] hover:!text-[#1D4ED8]">
                Privacy Policy
              </a>
              .
            </span>
          </label>
        </div>
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 delay-600 w-full duration-500 md:w-96">
          <button type="submit" disabled={isLoading || !consentChecked} className={`w-full rounded-md py-2 font-medium text-white transition-colors duration-150 ease-out will-change-auto md:w-96 md:py-2.5 ${!activeBtn || !consentChecked ? 'bg-[#318AE5]/50' : 'bg-[#318AE5] hover:bg-[#1D4ED8]'}`}>
            {isLoading ? <CgSpinner size={27} className="mx-auto animate-spin" /> : 'Send'}
          </button>
        </div>
      </form>
      {/* Toast  */}
      <div className={`fixed top-6 transition-transform duration-200 ease-out ${showToast !== 'none' ? 'right-5' : '-right-96'} z-50`}>
        <div className={`relative w-72 rounded-lg border-l-8 ${showToast === 'success' && 'border-green-500'} ${showToast === 'error' && 'border-red-500'} bg-slate-100 py-2.5 pl-4 shadow-lg`}>
          <button type="button" onClick={() => setShowToast('none')} className="absolute right-2 top-2 rounded p-0.5 text-left duration-200 hover:bg-gray-300" aria-label="Close">
            <CgClose />
          </button>
          <p className="text-sm font-semibold">
            {showToast === 'success' && 'Success'}
            {showToast === 'error' && 'Error'}
          </p>
          <p className="text-sm">
            {showToast === 'success' && formTypeMessages[formType]}
            {showToast === 'error' && (errMessage || 'Something went wrong!')}
          </p>
          <div className={`absolute bottom-0 left-0 h-1 ${showToast === 'success' && 'bg-green-300'} ${showToast === 'error' && 'bg-red-300'} ${showToast !== 'none' ? 'transition-width w-0 duration-[3200ms]' : 'w-full'}`} />
        </div>
      </div>
    </div>;
};
export default Contactus;